import { DeleteFilled, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Col, App as AntApp, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { deleteCountry, fetchCountryById } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const CountryLookupListDetail = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { modal } = AntApp.useApp();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const isLoading =
        lookupSlice.detailsPage.country.updateStatus === StatePieceStatus.IsFetching ||
        lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.IsFetching;
    const itemId = params.id ? parseInt(params.id) : undefined;

    const removeCountry = () => {
        modal.confirm({
            title: 'Delete country',
            icon: <ExclamationCircleOutlined />,
            content: "Are you sure you want to delete this country? This action can't be undone.",
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: () => {
                if (itemId) dispatch(deleteCountry(itemId));
            },
        });
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.country.deleteStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/country', { replace: true });
        }
    }, [lookupSlice.detailsPage.country.deleteStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchCountryById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    if (lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <React.Fragment>
            <ContentHeader
                title={`Country #${params.id}`}
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Country',
                        path: '/admin/lookup-lists/country',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.country.data?.code || '-',
                        path: `/admin/lookup-lists/country/${params.id}`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.country.fetchStatus) && (
                    <>
                        <Button type="primary" danger icon={<DeleteFilled />} loading={isLoading} onClick={() => removeCountry()}>
                            Delete option
                        </Button>
                        <Button
                            type="default"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/admin/lookup-lists/country/${params.id}/edit`)}
                        >
                            Edit option details
                        </Button>
                    </>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.country.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.country.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.country.fetchStatus} about={'Country'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Country option Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={6}>
                                    <label className="property required">Id</label>
                                    <p>{lookupSlice.detailsPage.country?.data?.id || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property required">Code</label>
                                    <p>{lookupSlice.detailsPage.country?.data?.code || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property required">Description</label>
                                    <p>{lookupSlice.detailsPage.country?.data?.description || '-'}</p>
                                </Col>
                                <Col span={6}>
                                    <label className="property required">Default currency</label>
                                    <p>{lookupSlice.detailsPage.country?.data?.defaultCurrency?.description || '-'}</p>
                                </Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.country.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.country.data?.created
                                            ? dayjs(lookupSlice.detailsPage.country.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.country.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.country.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.country.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </React.Fragment>
    );
};
